import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ChevronDown } from '../icon/solar-icons.js'
import './portal-dropdown.css'

export interface PortalDropdownOption {
    value: string
    label: string
    depth?: number
}

export interface PortalDropdownProps {
    options: PortalDropdownOption[]
    value: string
    onChange: (value: string) => void
    placeholder?: string
    alignRight?: boolean
}

export const PortalDropdown = ({
    options,
    value,
    onChange,
    placeholder = '—',
    alignRight = false,
}: PortalDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [focusIndex, setFocusIndex] = useState(-1)
    const containerRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    const selectedOption = options.find((o) => o.value === value)
    const displayLabel = selectedOption?.label ?? placeholder

    // Click-outside close
    useEffect(() => {
        if (!isOpen) return

        const handleClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [isOpen])

    // Keyboard navigation
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!isOpen) {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                    e.preventDefault()
                    setIsOpen(true)
                    setFocusIndex(0)
                }
                return
            }

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault()
                    setFocusIndex((prev) => Math.min(prev + 1, options.length - 1))
                    break
                case 'ArrowUp':
                    e.preventDefault()
                    setFocusIndex((prev) => Math.max(prev - 1, 0))
                    break
                case 'Enter':
                case ' ':
                    e.preventDefault()
                    if (focusIndex >= 0 && focusIndex < options.length) {
                        onChange(options[focusIndex].value)
                        setIsOpen(false)
                    }
                    break
                case 'Escape':
                    e.preventDefault()
                    setIsOpen(false)
                    break
                case 'Home':
                    e.preventDefault()
                    setFocusIndex(0)
                    break
                case 'End':
                    e.preventDefault()
                    setFocusIndex(options.length - 1)
                    break
            }
        },
        [isOpen, focusIndex, options, onChange],
    )

    // Scroll focused item into view
    useEffect(() => {
        if (!isOpen || focusIndex < 0) return
        const list = listRef.current
        if (!list) return
        const item = list.children[focusIndex] as HTMLElement | undefined
        item?.scrollIntoView({ block: 'nearest' })
    }, [isOpen, focusIndex])

    const handleSelect = (optionValue: string) => {
        onChange(optionValue)
        setIsOpen(false)
    }

    return (
        <div
            ref={containerRef}
            className={classNames(
                'portal-dropdown',
                isOpen && 'is-open',
                alignRight && 'portal-dropdown--align-right',
            )}
        >
            <button
                type="button"
                className="portal-dropdown__trigger"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={() => {
                    setIsOpen(!isOpen)
                    if (!isOpen) setFocusIndex(options.findIndex((o) => o.value === value))
                }}
                onKeyDown={handleKeyDown}
            >
                <span className="portal-dropdown__trigger-label">{displayLabel}</span>
                <ChevronDown className="portal-dropdown__chevron" />
            </button>

            {isOpen && (
                <div
                    ref={listRef}
                    className="portal-dropdown__panel"
                    role="listbox"
                    aria-activedescendant={
                        focusIndex >= 0 ? `portal-dd-opt-${focusIndex}` : undefined
                    }
                >
                    {options.map((option, idx) => (
                        <button
                            key={option.value}
                            id={`portal-dd-opt-${idx}`}
                            type="button"
                            role="option"
                            aria-selected={option.value === value}
                            data-depth={option.depth ?? 0}
                            className={classNames(
                                'portal-dropdown__option',
                                option.value === value && 'is-selected',
                                idx === focusIndex && 'is-focused',
                            )}
                            onClick={() => handleSelect(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
